import { useEffect, useRef } from 'react';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { useVesselStore } from 'src/context/VesselContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import { useAuthStore } from 'src/context/AuthContext';
import useApi from '../useApi';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { copyNewStowagePlanMetaData, createEmpyStowagePlan, mapStowagePlan, mapStowagePlanBM } from 'src/factory/Stowage/StowagePlanFactory';
import { useStowageUpdateHander } from 'src/app/useStowageUpdateHander';
import {
    addCargoesToStowagePlanChanges,
    addPortCallsToStowagePlanChanges,
    addViolationsToStowagePlanChanges,
    mapStowagePlanChangeBMs,
    mapStowagePlanChanges,
} from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowagePlanChangeBM } from 'src/types/BusinessModel/Stowage/StowagePlanChangeBM';
import { UUID } from 'crypto';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { StowagePlanRulesManager, StowageRulesOptions } from 'src/utils/stowage/stowageRulesManager/stowagePlanRulesManager';
import { useRotationStore } from 'src/context/RotationContext';
import { updateViolations } from 'src/utils/stowage/violations/violationsUtils';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { DEFAULT_STOWAGE_PARAMS, STOWAGE_PARAMS } from 'src/utils/navigation/stowageNavigationConstants';
import { getChangedPortCallStatus, getPortCallDepartureLockReason } from 'src/utils/stowage/portCallDepartureUtils';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';

interface PendingSave {
    stowagePlan: StowagePlan;
    stowagePlanChanges: StowagePlanChange[];
    saveId?: string;
    options?: StowageSaveOptions;
    resolve: (result: SaveStowageDataResult) => void;
    reject: (error: Error) => void;
}

interface StowageSaveOptions {
    // Return false if navigation or explicit exit has superseded this view update.
    beforePlanUpdate?: (plan: StowagePlan) => boolean;
}

interface StowageApiQueryParams {
    imoNumber: number;
    connectionId: string;
    stowagePlanId?: string;
    _saveId?: string;
}

interface ProcessStowagePlanResult {
    updatedStowagePlan: StowagePlan;
    currentStowagePlans?: StowagePlan[];
    updatedChanges: StowagePlanChange[];
    wasNewPlanCreated?: boolean;
}

interface SaveStowageDataResult {
    savedStowagePlanChanges: StowagePlanChange[];
    updatedStowagePlan: StowagePlan;
}

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { selectedVessel } = useVesselStore();
    const { violations } = useViolationsStore();
    const { signalRConnectionId, loggedInUser } = useAuthStore();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, postApiData } = useApi();
    const { updateStowagePlansOnAdd, updateStowagePlanChanges, updateActiveStowagePlan, updateSelectedStowageItems, deleteStowagePlanFromState } =
        useStowageUpdateHander();
    const { coatingCompatibilities, uscgCompatibilities } = useRotationStore();
    const { stowagePlans, setStowagePlans, setActiveStowagePlan } = useStowagePlanStore();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();
    const { getQueryParamValueFromUrl, triggerQueryParamsUpdate } = useQueryParamsHandler();

    // Save queue to prevent concurrent saves that can cause duplicate stowage plan inserts
    const isSavingRef = useRef<boolean>(false);
    const saveQueueRef = useRef<PendingSave[]>([]);

    useEffect(() => {
        if (state.status !== 'success') {
            processApiEndpointError(state);
        }
    }, [state]);

    const updateStowagePlanMetadata = (plan: StowagePlan): StowagePlan => {
        const _updatedPlan = { ...plan };
        _updatedPlan.lastUpdatedUtc = getDateNow();
        _updatedPlan.lastUpdatedBy = loggedInUser.userName;
        return _updatedPlan;
    };

    const getStowageApiQueryParams = (stowagePlanId?: string, saveId?: string): StowageApiQueryParams => {
        const queryParams: StowageApiQueryParams = {
            imoNumber: selectedVessel.imoNumber,
            connectionId: signalRConnectionId,
        };

        if (stowagePlanId) {
            queryParams.stowagePlanId = stowagePlanId;
        }

        if (saveId) {
            queryParams._saveId = saveId;
        }

        return queryParams;
    };

    // Signals to useSaveButtonStatus that the save request has reached the network, so the
    // button can move from "saving" to "saved-pending" while it waits for the service worker
    // to confirm the background request.
    const dispatchSaveApiFired = (saveId?: string): void => {
        if (!saveId) return;
        window.dispatchEvent(new CustomEvent('save-api-fired', { detail: { saveId } }));
    };

    // Signals that the save never reached (or failed before reaching) the network, so the
    // button resets immediately instead of waiting for the hook's watchdog timeout.
    const dispatchSaveAborted = (saveId?: string): void => {
        if (!saveId) return;
        window.dispatchEvent(new CustomEvent('save-aborted', { detail: { saveId } }));
    };

    const addStowagePlan = async (stowagePlan: StowagePlan, skipSetActiveStowagePlan?: boolean) => {
        const updatedPlan = updateStowagePlanMetadata(stowagePlan);
        const _bm = mapStowagePlanBM(updatedPlan);
        const result = await postApiData({
            endpoint: apiEndpoints['addStowagePlan'].url,
            data: _bm,
            queryParam: buildQueryParams(getStowageApiQueryParams(updatedPlan.stowagePlanGuid)),
            shouldReturnError: true,
            callInBackground: true,
        });

        if (result.status && result.status !== 200) {
            throw new Error(apiEndpoints['addStowagePlan'].errorMessage);
        }

        const bm = result as StowagePlanBM;
        const _stowagePlan = mapStowagePlan(bm);
        _stowagePlan.isNewStowagePlan = true;

        return updateStowagePlansOnAdd(_stowagePlan, undefined, skipSetActiveStowagePlan);
    };

    const updateStowagePlan = async (stowagePlan: StowagePlan, skipSetActiveStowagePlan?: boolean) => {
        const updatedPlan = updateStowagePlanMetadata(stowagePlan);
        const _bm = mapStowagePlanBM(updatedPlan);
        const isDelete = !!updatedPlan.isDeleted;
        const result = await postApiData({
            endpoint: apiEndpoints['updateStowagePlan'].url,
            data: _bm,
            queryParam: buildQueryParams(getStowageApiQueryParams(updatedPlan.stowagePlanGuid)),
            shouldReturnError: true,
            callInBackground: !isDelete,
            additionalInfo: isDelete ? { suppressGenericErrorToast: true } : undefined,
        });

        if (result instanceof Error) {
            throw result;
        }

        if (result?.status && result.status !== 200) {
            throw new Error(apiEndpoints['updateStowagePlan'].errorMessage);
        }

        if (isDelete) {
            return deleteStowagePlanFromState(updatedPlan);
        }

        const bm = result as StowagePlanBM;
        const _stowagePlan = mapStowagePlan(bm);

        return updateStowagePlansOnAdd(_stowagePlan, stowagePlan, skipSetActiveStowagePlan);
    };

    const checkViolations = (stowagePlan: StowagePlan) => {
        const stowageRulesManager = new StowagePlanRulesManager();
        const newViolations = stowageRulesManager.verifyStowageRules(
            {} as StowageRulesOptions,
            stowagePlan,
            selectedVessel,
            null,
            null,
            coatingCompatibilities,
            uscgCompatibilities
        );
        return updateViolations(violations ?? [], newViolations);
    };

    const executeSaveStowageData = async (
        stowagePlan: StowagePlan,
        stowagePlanChanges: StowagePlanChange[],
        saveId?: string,
        options?: StowageSaveOptions
    ): Promise<SaveStowageDataResult> => {
        if (!stowagePlanChanges?.length) {
            dispatchSaveAborted(saveId);
            return { savedStowagePlanChanges: [], updatedStowagePlan: stowagePlan };
        }

        const originalStowagePlanGuid = stowagePlan.stowagePlanGuid;
        const departingCalls = stowagePlanChanges
            .filter((change) => change.changeDataType === StowageChangeDataType.RotationPortCall && !change.isDeleted)
            .filter((change) => getChangedPortCallStatus(change) === PortCallStatusEnum.Departed)
            .map((change) => stowagePlan.rotation.find((call) => call.portCallGuid === change.changeDataGuid))
            .filter((call): call is RotationPortCall => Boolean(call));
        if (departingCalls.length) {
            if (stowagePlan.manifest) stowagePlan.manifest.cargoSequenceSnapshots = undefined;
            const currentViolations = new StowagePlanRulesManager().verifyStowageRules(
                {} as StowageRulesOptions,
                stowagePlan,
                selectedVessel,
                null,
                null,
                coatingCompatibilities,
                uscgCompatibilities
            );
            for (const call of departingCalls) {
                const reason = getPortCallDepartureLockReason(call, stowagePlan.rotation, currentViolations);
                if (reason) throw new Error(reason);
            }
        }
        const originalRotation = stowagePlan.rotation;
        let currentStowagePlans = [...stowagePlans];
        let accumulatedChanges: StowagePlanChange[] = [];
        let allChanges: StowagePlanChange[] = [];

        const processedViolations = processViolations(stowagePlan);
        const explicitViolationGuids = new Set(
            stowagePlanChanges.filter((change) => change.changeDataType === StowageChangeDataType.Violation).map((change) => change.changeDataGuid)
        );
        accumulatedChanges = processedViolations.filter((change) => !explicitViolationGuids.has(change.changeDataGuid));

        const processResult = await processStowagePlanState(stowagePlan, accumulatedChanges);
        stowagePlan = processResult.updatedStowagePlan;
        currentStowagePlans = processResult.currentStowagePlans || currentStowagePlans;
        accumulatedChanges = processResult.updatedChanges;
        const wasNewPlanCreated = processResult.wasNewPlanCreated ?? false;

        allChanges = [...accumulatedChanges, ...stowagePlanChanges];

        // Update stowagePlanGuid on all changes when a new plan was created
        // This ensures the changes are associated with the new child plan, not the original master plan
        if (wasNewPlanCreated) {
            allChanges.forEach((change) => {
                change.stowagePlanGuid = stowagePlan.stowagePlanGuid;
            });
        }

        setTransactionGuid(allChanges, crypto.randomUUID());

        const changesToSend = mapStowagePlanChangeBMs(allChanges, loggedInUser.fullName, stowagePlan.stowagePlanGuid);

        // SW dependency gate ensures AddStowagePlan completes before SaveStowageData reaches the backend,
        // so we always fire in background and let the optimistic response handle the UX.
        const saveRequest = postApiData({
            endpoint: apiEndpoints['saveStowageData'].url,
            data: changesToSend,
            queryParam: buildQueryParams(getStowageApiQueryParams(stowagePlan.stowagePlanGuid.toString(), saveId)),
            additionalInfo: { changes: allChanges },
            shouldReturnError: true,
            callInBackground: true,
        });

        dispatchSaveApiFired(saveId);
        const saveResponse = await saveRequest;
        if (saveResponse instanceof Error) throw saveResponse;
        if (saveResponse?.status && saveResponse.status !== 200) {
            throw new Error(apiEndpoints['saveStowageData'].errorMessage);
        }

        // Apply changes to local state — must complete before returning so callers
        // (e.g. ChangeLog sync) see the populated plan and don't overwrite it.
        // Also awaits mapRotation (via updateStowagePlanChanges) so rotation/berthCalls are
        // populated before selection is restored below.
        const stowagePlanForUpdate = stowagePlan;
        const { updatedStowagePlan: rebuiltPlan } = await updateStowagePlanChanges(allChanges, stowagePlanForUpdate, currentStowagePlans);
        rebuiltPlan.isNewStowagePlan = null;
        const preserveBerthSelection = options?.beforePlanUpdate?.(rebuiltPlan) ?? false;
        if (options?.beforePlanUpdate && !preserveBerthSelection) return { savedStowagePlanChanges: allChanges, updatedStowagePlan: rebuiltPlan };
        updateSelectedStowageItems(rebuiltPlan);
        handleStowagePlanReference(originalStowagePlanGuid, rebuiltPlan, originalRotation, preserveBerthSelection);
        setActiveStowagePlan({ ...rebuiltPlan });

        return { savedStowagePlanChanges: allChanges, updatedStowagePlan: rebuiltPlan };
    };

    const processNextSave = async (): Promise<void> => {
        if (isSavingRef.current || saveQueueRef.current.length === 0) {
            return;
        }

        isSavingRef.current = true;
        const pendingSave = saveQueueRef.current.shift()!;

        try {
            const result = await executeSaveStowageData(
                pendingSave.stowagePlan,
                pendingSave.stowagePlanChanges,
                pendingSave.saveId,
                pendingSave.options
            );
            pendingSave.resolve(result);
        } catch (error) {
            dispatchSaveAborted(pendingSave.saveId);
            pendingSave.reject(error instanceof Error ? error : new Error(String(error)));
        } finally {
            isSavingRef.current = false;
            // Process next queued save if any
            processNextSave();
        }
    };

    const saveStowageData = async (
        stowagePlan: StowagePlan,
        stowagePlanChanges: StowagePlanChange[],
        saveId?: string,
        options?: StowageSaveOptions
    ): Promise<SaveStowageDataResult> => {
        // Fast path: if no changes, return immediately without queuing
        if (!stowagePlanChanges?.length) {
            dispatchSaveAborted(saveId);
            return { savedStowagePlanChanges: [], updatedStowagePlan: stowagePlan };
        }

        return new Promise<SaveStowageDataResult>((resolve, reject) => {
            saveQueueRef.current.push({
                stowagePlan,
                stowagePlanChanges,
                saveId,
                options,
                resolve,
                reject,
            });
            processNextSave();
        });
    };

    const processViolations = (stowagePlan: StowagePlan): StowagePlanChange[] => {
        const violationChanges: StowagePlanChange[] = [];
        const checkViolationsResponse = checkViolations(stowagePlan);

        if (checkViolationsResponse.newViolations.length > 0) {
            addViolationsToStowagePlanChanges(violationChanges, stowagePlan.stowagePlanGuid, checkViolationsResponse.newViolations, false);
        }
        if (checkViolationsResponse.deletedViolations.length > 0) {
            addViolationsToStowagePlanChanges(violationChanges, stowagePlan.stowagePlanGuid, checkViolationsResponse.deletedViolations, true);
        }

        return violationChanges;
    };

    const processStowagePlanState = async (stowagePlan: StowagePlan, changes: StowagePlanChange[]): Promise<ProcessStowagePlanResult> => {
        const updatedPlan = updateStowagePlanMetadata(stowagePlan);
        const updatedChanges = [...changes];

        if (updatedPlan.isNewStowagePlan) {
            const { updatedStowagePlan } = await addStowagePlan(updatedPlan);
            const processedPlan = copyNewStowagePlanMetaData(stowagePlan, updatedStowagePlan);

            addPortCallsToStowagePlanChanges(updatedChanges, processedPlan.stowagePlanGuid, processedPlan.rotation, false);
            addCargoesToStowagePlanChanges(updatedChanges, processedPlan.stowagePlanGuid, processedPlan.cargoes, false);

            return { updatedStowagePlan: processedPlan, updatedChanges, wasNewPlanCreated: true };
        } else if (updatedPlan.type === StowagePlanTypeEnum.Master || updatedPlan.type === StowagePlanTypeEnum.Published) {
            const emptyPlan = createEmpyStowagePlan(
                selectedVessel.imoNumber,
                loggedInUser.userName,
                updatedPlan.stowagePlanGuid,
                updatedPlan.title,
                updatedPlan.masterPlanVersion ?? 0
            );
            // Preserve the IMOS sync timestamp when copy-on-write creates a new draft from a master plan.
            // This lets sync flows persist their metadata on create instead of needing an immediate follow-up update.
            const updatedEmptyPlan = updateStowagePlanMetadata({
                ...emptyPlan,
                syncedWithImosDateTimeUtc: updatedPlan.syncedWithImosDateTimeUtc ?? null,
            });

            const { updatedStowagePlan, updatedStowagePlans } = await addStowagePlan(updatedEmptyPlan, true);

            // Populate new draft from original plan data (rotation, cargoes, stowageItems, etc.)
            // while keeping the new draft's metadata (GUID, type, timestamps, etc.)
            const populatedPlan: StowagePlan = {
                ...stowagePlan,
                stowagePlanGuid: updatedStowagePlan.stowagePlanGuid,
                parentGuid: updatedStowagePlan.parentGuid,
                title: updatedStowagePlan.title,
                comments: updatedStowagePlan.comments,
                type: updatedStowagePlan.type,
                shareType: updatedStowagePlan.shareType,
                owner: updatedStowagePlan.owner,
                initials: updatedStowagePlan.initials,
                lastUpdatedBy: updatedStowagePlan.lastUpdatedBy,
                lastUpdatedUtc: updatedStowagePlan.lastUpdatedUtc,
                createdDateTimeUtc: updatedStowagePlan.createdDateTimeUtc,
                syncedWithParentDateTimeUtc: updatedStowagePlan.syncedWithParentDateTimeUtc,
                syncedWithImosDateTimeUtc: updatedStowagePlan.syncedWithImosDateTimeUtc,
                masterPlanVersion: updatedStowagePlan.masterPlanVersion,
                isNewStowagePlan: true,
            };

            return {
                updatedStowagePlan: populatedPlan,
                currentStowagePlans: updatedStowagePlans,
                updatedChanges,
                wasNewPlanCreated: true,
            };
        } else {
            // Find the current plan in the store and update it
            const existingPlanIndex = stowagePlans.findIndex((plan) => plan.stowagePlanGuid === updatedPlan.stowagePlanGuid);

            if (existingPlanIndex !== -1) {
                const updatedStowagePlans = [...stowagePlans];
                updatedStowagePlans[existingPlanIndex] = updatedPlan;
                setStowagePlans([...updatedStowagePlans]);
            }
        }

        return {
            updatedStowagePlan: updatedPlan,
            updatedChanges,
        };
    };

    const handleStowagePlanReference = (
        originalGuid: UUID,
        updatedPlan: StowagePlan,
        originalRotation?: RotationPortCall[],
        preserveBerthSelection = false
    ): void => {
        if (updatedPlan.stowagePlanGuid !== originalGuid) {
            // New child plan created — try to remap portCallGuid to the equivalent port call in the new plan
            const currentPortCallGuid = getQueryParamValueFromUrl('portCallGuid');
            if (currentPortCallGuid && originalRotation?.length && updatedPlan.rotation?.length) {
                const oldPortCall = originalRotation.find((pc) => pc.portCallGuid === currentPortCallGuid);
                if (oldPortCall?.imosPortCallId) {
                    const newPortCall = updatedPlan.rotation.find((pc) => pc.imosPortCallId === oldPortCall.imosPortCallId);
                    if (newPortCall) {
                        const berthGuid = preserveBerthSelection ? getQueryParamValueFromUrl(STOWAGE_PARAMS.BERTH_CALL_GUID) : null;
                        const berth = newPortCall.berthCalls?.find((call) => call.berthCallGuid === berthGuid);
                        // Sequencing retains its berth while the first save creates a draft.
                        triggerQueryParamsUpdate(
                            [
                                { name: STOWAGE_PARAMS.STOWAGE_PLAN_GUID, newValue: updatedPlan.stowagePlanGuid },
                                { name: STOWAGE_PARAMS.PORT_CALL_GUID, newValue: newPortCall.portCallGuid },
                                ...(berth ? [{ name: STOWAGE_PARAMS.BERTH_CALL_GUID, newValue: berth.berthCallGuid }] : []),
                            ],
                            // Only preserve unrelated defaults here. Keeping the params we're actively
                            // updating in clearAllExcept lets the query manager replay stale values.
                            { clearAllExcept: [...DEFAULT_STOWAGE_PARAMS] }
                        );
                        return;
                    }
                }
            }
            // Fallback: no remapping possible — preserve old portCallGuid (existing behavior)
            setQueryParamStowagePlan(updatedPlan.stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
        } else {
            updateActiveStowagePlan({ ...updatedPlan });
        }
    };

    const setTransactionGuid = (stowagePlanChanges: StowagePlanChange[], transactionGuid: UUID): void =>
        stowagePlanChanges.forEach((spc) => (spc.transactionGuid = transactionGuid));

    return {
        addStowagePlan,
        updateStowagePlan,
        saveStowageData,
    };
};
